/**
 * Web design tokens, and the self-hosted faces they name.
 *
 * Imported from `app/_layout.tsx`. It was imported NOWHERE until 951ae2d5,
 * which meant every `var(--font-*)` below was undefined, every `font-family`
 * on web was an invalid declaration the browser dropped, and the whole web app
 * silently rendered in a fallback face.
 *
 * ## Why the files are self-hosted and not loaded from a CDN
 *
 * Google's CDN sends every visitor's IP to a third party on page load, before
 * any consent. This app deliberately keeps its error data in the EU
 * (`ingest.de.sentry.io`) and ships a consent banner, so that is the wrong
 * trade. It would also need `fonts.gstatic.com` in `font-src` and
 * `fonts.googleapis.com` in `style-src` permanently, weakening a policy staged
 * for promotion to enforcing — whereas `font-src 'self'` already allows these.
 * And a CDN cannot serve the native apps at all, so the files would be needed
 * regardless.
 *
 * Both faces are OFL-licensed; self-hosting is expressly permitted. Latin
 * subsets only, which covers every locale this app ships (en, es, fr, pt).
 *
 * ## Why these two faces
 *
 * `Spline Sans` was already first in the sans stack — self-hosting it makes the
 * existing design intent real instead of aspirational.
 *
 * `Gelasio` is metric-compatible with Georgia, which the serif stack named. So
 * the fallback occupies the same space as the webfont and the swap costs
 * little or no layout shift — the app measures CLS 0.000 today and that is
 * worth protecting.
 *
 * Both are VARIABLE fonts: one file per family covers the whole weight axis, so
 * bold is a real weight rather than a synthetic one. That matters beyond
 * looks — the shareable poster is rendered by html2canvas, which measures text
 * against the DOM and then redraws it onto a canvas, and synthetic bold is a
 * place those two can disagree and shove words into each other.
 */

@font-face {
  font-family: 'Spline Sans';
  src: url('/fonts/SplineSans-latin.14ed7b4d.woff2') format('woff2');
  font-weight: 300 700;
  font-style: normal;
  /* `swap`, not `optional`: the poster export must actually get the real face,
     and `optional` allows the browser to decline it on a slow connection. */
  font-display: swap;
  unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA,
    U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193,
    U+2212, U+2215, U+FEFF, U+FFFD;
}

@font-face {
  font-family: 'Gelasio';
  src: url('/fonts/Gelasio-latin.f4a2c06e.woff2') format('woff2');
  font-weight: 400 700;
  font-style: normal;
  font-display: swap;
  unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA,
    U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193,
    U+2212, U+2215, U+FEFF, U+FFFD;
}

:root {
  --font-display:
    Spline Sans, Inter, ui-sans-serif, system-ui, sans-serif, Apple Color Emoji, Segoe UI Emoji,
    Segoe UI Symbol, Noto Color Emoji;
  --font-mono:
    ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace;
  --font-rounded: 'SF Pro Rounded', 'Hiragino Maru Gothic ProN', Meiryo, 'MS PGothic', sans-serif;
  /* Gelasio first, Georgia behind it: the two are metric-compatible, so the
     fallback holds the same space while the webfont loads. */
  --font-serif: Gelasio, Georgia, 'Times New Roman', serif;
}
